import { Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { useState } from "react";
import { AppDialog, DialogActionsBar, DialogCheckbox, DialogField } from "@/components/app-dialog";
import { FuiButton, FuiDropdown, FuiInput } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    maxHeight: "240px",
    overflow: "auto",
    minWidth: 0,
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  plans: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
});

export function PlanMemberAddDialog({
  open,
  people,
  onClose,
  onAdd,
  onInviteGuest,
}: {
  open: boolean;
  people: { id: string; name: string; email: string; teamName: string }[];
  onClose: () => void;
  onAdd: (ids: string[]) => void;
  /** 워크스페이스에 없는 이메일. 실패 이유를 반환하면 그 문장을 보여 준다. */
  onInviteGuest: (email: string) => string | null;
}) {
  const styles = useStyles();
  const [picked, setPicked] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const toggle = (id: string, on: boolean) => {
    setPicked((curr) => (on ? [...curr, id] : curr.filter((item) => item !== id)));
  };

  const submit = () => {
    const address = email.trim();
    if (address) {
      const failed = onInviteGuest(address);
      if (failed) {
        setError(failed);
        return;
      }
    }
    if (picked.length) onAdd(picked);
    if (!address && picked.length === 0) {
      setError("사람을 고르거나 이메일을 입력하세요.");
      return;
    }
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="md"
      title="멤버 추가"
      description="워크스페이스 멤버를 이 플랜에 편집자로 넣습니다."
      footer={<DialogActionsBar onCancel={onClose} onConfirm={submit} confirmLabel="추가" />}
    >
      {people.length === 0 ? (
        <GhostEmptyState compact title="추가할 멤버가 없습니다" description="이미 이 플랜에 있거나, 활성 멤버가 없습니다." />
      ) : (
        <div className={styles.list}>
          {people.map((person) => (
            <DialogCheckbox
              key={person.id}
              checked={picked.includes(person.id)}
              onChange={(on) => toggle(person.id, on)}
              label={
                <span>
                  {person.name}
                  <Caption1 className={styles.quiet}>
                    {" "}
                    {person.teamName || "소속 없음"} · {person.email}
                  </Caption1>
                </span>
              }
            />
          ))}
        </div>
      )}
      <DialogField label="워크스페이스에 없는 사람">
        <FuiInput
          density="dialog"
          value={email}
          aria-label="게스트 이메일"
          placeholder="게스트 이메일"
          onChange={(_, data) => {
            setEmail(data.value);
            setError("");
          }}
        />
        <Caption1 className={styles.quiet}>이 플랜만 보는 게스트로 초대합니다.</Caption1>
      </DialogField>
      {error ? <Caption1 className={styles.quiet}>{error}</Caption1> : null}
    </AppDialog>
  );
}

export function PlanTeamAddDialog({
  open,
  teams,
  onClose,
  onAdd,
}: {
  open: boolean;
  teams: { id: string; name: string; count: number }[];
  onClose: () => void;
  onAdd: (teamId: string) => void;
}) {
  const styles = useStyles();
  const [teamId, setTeamId] = useState(teams[0]?.id ?? "");
  const chosen = teams.find((team) => team.id === teamId) ?? teams[0];
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="sm"
      title="팀 추가"
      description="그 팀의 활성 멤버를 이 플랜에 편집자로 넣습니다. 이후 팀 구성이 바뀌어도 플랜 멤버는 따라가지 않습니다."
      footer={
        <DialogActionsBar
          onCancel={onClose}
          onConfirm={() => {
            if (chosen) onAdd(chosen.id);
            onClose();
          }}
          confirmLabel="추가"
          disabled={!chosen || chosen.count === 0}
        />
      }
    >
      {teams.length === 0 ? (
        <GhostEmptyState compact title="팀이 없습니다" description="워크스페이스에서 팀을 만든 뒤에 넣을 수 있습니다." />
      ) : (
        <DialogField label="팀">
          <FuiDropdown
            density="dialog"
            aria-label="추가할 팀"
            value={chosen?.id ?? ""}
            options={teams.map((team) => ({ value: team.id, label: `${team.name} · ${team.count}` }))}
            onValueChange={setTeamId}
          />
          {chosen && chosen.count === 0 ? <Caption1 className={styles.quiet}>넣을 활성 멤버가 없습니다.</Caption1> : null}
        </DialogField>
      )}
    </AppDialog>
  );
}

export function AssignPlanDialog({
  open,
  plans,
  onClose,
  onPick,
}: {
  open: boolean;
  plans: { id: string; name: string }[];
  onClose: () => void;
  onPick: (planId: string) => void;
}) {
  const styles = useStyles();
  return (
    <AppDialog open={open} onClose={onClose} width="sm" title="작업을 배정할 플랜" description="이 사람이 배정된 플랜 중에서 고릅니다.">
      <div className={styles.plans}>
        {plans.map((plan) => (
          <FuiButton
            key={plan.id}
            appearance="subtle"
            onClick={() => {
              onPick(plan.id);
              onClose();
            }}
          >
            {plan.name}
          </FuiButton>
        ))}
      </div>
    </AppDialog>
  );
}

